"use client";

import SLogo from "@public/image/icon/logo.svg";
import { useMemo } from "react";
import { PATH } from "@/constants/path";
import { useBatchImpressionTracking } from "@/hooks/useImpressionTracking";
import { trackEvent } from "@/utils/analytics";

import Button from "./Button";

function Footer() {
  const impressionEvents = useMemo(
    () => [
      { eventName: "footer_impression", params: { link_text: "핵심 인재 리스트" } },
      { eventName: "footer_impression", params: { link_text: "AI 개인화 메시지" } },
      { eventName: "footer_impression", params: { link_text: "리스트형 상품" } },
      { eventName: "footer_impression", params: { link_text: "구독형 상품" } },
      { eventName: "footer_impression", params: { link_text: "서비스 프로세스" } },
      { eventName: "footer_impression", params: { link_text: "블로그" } },
      { eventName: "download_btn_impression", params: { file_name: "company_deck", cta_position: "footer" } },
      { eventName: "footer_impression", params: { link_text: "고객사 로그인" } },
      { eventName: "footer_impression", params: { link_text: "문의하기", cta_type: "inquiry" } },
      { eventName: "footer_impression", params: { link_text: "LinkedIn", outbound: true } },
      { eventName: "footer_impression", params: { link_text: "개인정보 처리방침" } },
      { eventName: "footer_impression", params: { link_text: "서비스 이용 약관" } },
    ],
    [],
  );

  const footerRef = useBatchImpressionTracking<HTMLElement>(impressionEvents);

  const handleFooterClick = (linkText: string, extra?: Record<string, string | boolean>) => {
    trackEvent("footer_click", { link_text: linkText, ...extra });
  };

  const handleDownloadClick = () => {
    trackEvent("file_download", { file_name: "company_deck", cta_position: "footer" });
  };

  return (
    <footer
      ref={footerRef}
      className="grid w-full grid-cols-1 justify-between gap-x-10 gap-y-12 bg-footer px-side py-8 font-extralight text-gray header:grid-cols-2 header:gap-y-24"
    >
      <div className="flex flex-col items-start gap-x-10 gap-y-4 header:flex-row">
        <SLogo className="w-32" />
        <div className="flex flex-col gap-2 text-sm header:text-base">
          <div>서치라이트AI 주식회사 | 대표: 양승모</div>
          <div>사업자등록번호: 826-81-03242</div>
          <div>서울 마포구 마포대로 122 13F</div>
          <div className="font-light">biz@searchright.net</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-10 text-sm tablet:grid-cols-3 header:gap-x-20 header:justify-self-end header:text-base">
        <div className="flex flex-col gap-2">
          <div className="mb-1 font-semibold">서비스</div>
          <a href={`${PATH.SERVICE}#service-2`} onClick={() => handleFooterClick("핵심 인재 리스트")}>
            핵심 인재 리스트
          </a>
          <a href={`${PATH.SERVICE}#service-3`} onClick={() => handleFooterClick("AI 개인화 메시지")}>
            AI 개인화 메시지
          </a>
        </div>
        <div className="flex flex-col gap-2">
          <div className="mb-1 font-semibold">가격</div>
          <a href={`${PATH.PRICING}#pricing`} onClick={() => handleFooterClick("리스트형 상품")}>
            리스트형 상품
          </a>
          <a href={`${PATH.PRICING}#pricing`} onClick={() => handleFooterClick("구독형 상품")}>
            구독형 상품
          </a>
          <a href={`${PATH.PRICING}#process`} onClick={() => handleFooterClick("서비스 프로세스")}>
            서비스 프로세스
          </a>
        </div>
        <div className="flex flex-col gap-2">
          <div className="mb-1 font-semibold">리소스</div>
          <a href={PATH.BLOG} onClick={() => handleFooterClick("블로그")}>
            블로그
          </a>
          <a href={PATH.COMPANY_INTRO} target="_blank" rel="noopener noreferrer" onClick={handleDownloadClick}>
            회사 소개서 다운받기
          </a>
        </div>
      </div>

      <div className="flex w-full flex-col gap-5 self-end justify-self-start header:order-4">
        <div className="ml-auto flex flex-wrap justify-end gap-x-3 gap-y-2">
          <Button
            className="cursor-pointer"
            color="gray"
            size="sm"
            href={PATH.CLIENT_LOGIN}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleFooterClick("고객사 로그인")}
          >
            고객사 로그인
          </Button>
          <Button
            className="cursor-pointer"
            color="gray"
            size="sm"
            href={PATH.REQUEST}
            onClick={() => handleFooterClick("문의하기", { cta_type: "inquiry" })}
          >
            문의하기
          </Button>
          <Button
            className="cursor-pointer"
            color="gray"
            size="sm"
            href={PATH.LINKEDIN}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleFooterClick("LinkedIn", { outbound: true })}
          >
            LinkedIn
          </Button>
        </div>
        <p className="hidden text-end header:block">세상 모든 기업이 최고의 AI 헤드헌터를 소유하는 세상, 서치라이트AI</p>
      </div>

      <div className="flex flex-col items-end gap-3 self-end text-sm max-header:justify-self-end header:order-3 header:items-start header:text-base">
        <div className="flex gap-5 header:mr-auto">
          <a
            href={PATH.PRIVACY}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold"
            onClick={() => handleFooterClick("개인정보 처리방침")}
          >
            개인정보 처리방침
          </a>
          <a
            href={PATH.TERMS}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold"
            onClick={() => handleFooterClick("서비스 이용 약관")}
          >
            서비스 이용 약관
          </a>
        </div>
        <p className="text-start text-sm">&copy; 2024 SearcHRight. All rights reserved.</p>
      </div>
    </footer>
  );
}

export default Footer;
